import React, { useEffect, useState } from 'react';
import { getToken } from '../utils/tokenHelper';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';

const EditProfileModal = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        date_of_birth: '',
        address: '',
        gender: 'male'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);

    useEffect(() => {
        if (isOpen && initialLoad) {
            fetchProfileData();
            setInitialLoad(false);
        }
    }, [isOpen, initialLoad]);

    const fetchProfileData = async () => {
        const token = getToken();
        if (!token) {
            setError('No token found');
            return;
        }

        try {
            const myHeaders = new Headers();
            myHeaders.append("Accept", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const response = await fetch("https://devapi.quickcare.ng/api/v1/user/profile", {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow'
            });

            if (!response.ok) {
                throw new Error('Failed to fetch profile data');
            }

            const result = await response.json();
            const profile = result.data;
            
            setFormData({
                first_name: profile.first_name || '',
                last_name: profile.last_name || '',
                date_of_birth: profile.date_of_birth || '',
                address: profile.address || '',
                gender: profile.gender?.toLowerCase() || 'male'
            });
        } catch (err) {
            console.error('Error fetching profile:', err);
            setError(err.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleGenderChange = (gender) => {
        setFormData(prev => ({
            ...prev,
            gender
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        const token = getToken();
        if (!token) {
            setError('No token found');
            setLoading(false);
            return;
        }

        try {
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");
            myHeaders.append("Authorization", `Bearer ${token}`);

            const requestBody = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                date_of_birth: formData.date_of_birth,
                address: formData.address,
                gender: formData.gender
            };

            const response = await fetch("https://devapi.quickcare.ng/api/v1/user/profile", {
                method: "PUT",
                headers: myHeaders,
                body: JSON.stringify(requestBody),
                redirect: "follow"
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update profile');
            }

            // Close the modal after successful submission
            onClose();
            // Optionally, you might want to refresh the parent component here
        } catch (err) {
            console.error('Error updating profile:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 instrument-sans-medium">
            <div className="bg-white p-6 rounded-md w-full max-w-md mx-4">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold">Edit Profile</h2>
                    <button 
                        onClick={onClose} 
                        className="text-gray-500 hover:text-gray-700"
                        disabled={loading}
                    >
                        <CloseIcon />
                    </button>
                </div>
                
                {error && (
                    <div className="mt-4 p-3 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                <div className="mt-4">
                    <form onSubmit={handleSubmit}>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">First Name</label>
                            <input
                                type="text"
                                name="first_name"
                                className="mt-1 block w-full border border-gray-300 rounded outline-0 p-2"
                                value={formData.first_name}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Last Name</label>
                            <input
                                type="text"
                                name="last_name"
                                className="mt-1 block w-full border border-gray-300 rounded outline-0 p-2"
                                value={formData.last_name}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                            <input
                                type="date"
                                name="date_of_birth"
                                className="mt-1 block w-full border border-gray-300 rounded outline-0 p-2"
                                value={formData.date_of_birth}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Address</label>
                            <input
                                type="text"
                                name="address"
                                className="mt-1 block w-full border border-gray-300 rounded outline-0 p-2"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Gender</label>
                            <div className="mt-1">
                                <label className="inline-flex items-center">
                                    <input
                                        type="radio"
                                        className="form-radio h-4 w-4 text-[#069125]"
                                        name="gender"
                                        checked={formData.gender === 'male'}
                                        onChange={() => handleGenderChange('male')}
                                        disabled={loading}
                                    />
                                    <span className="ml-2">Male</span>
                                </label>
                                <label className="inline-flex items-center ml-6">
                                    <input
                                        type="radio"
                                        className="form-radio h-4 w-4 text-[#069125]"
                                        name="gender"
                                        checked={formData.gender === 'female'}
                                        onChange={() => handleGenderChange('female')}
                                        disabled={loading}
                                    />
                                    <span className="ml-2">Female</span>
                                </label>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-[#069125] text-white font-medium rounded-md hover:bg-[#057a20] flex items-center justify-center min-w-20"
                                disabled={loading}
                            >
                                {loading ? (
                                    <CircularProgress size={20} color="inherit" />
                                ) : (
                                    'Save'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default EditProfileModal;